import React, { lazy, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { hasValue } from 'globalutils/fmUtils';
import {
  getCurrentDriverLabel,
  mapCoDriversToOptions,
} from './coDriverEventReassignmentUtils';

const layout = window?.$environment?.CURRENT_LAYOUT;
const Input = lazy(() => import(`globalcomponents/UI/Input/${layout}`));
const Label = lazy(() => import(`globalcomponents/UI/FormLabel/${layout}`));
const Message = lazy(() => import(`globalcomponents/UI/Message/${layout}`));
const Button = lazy(() => import(`globalcomponents/UI/Button/${layout}`));
const Select = lazy(() => import(`globalcomponents/UI/Select/${layout}`));
const FormStyle = require(`globalcomponents/UI/Form/${layout}/serverStyle`);
const Modal = require(`globalcomponents/UI/Modal/${layout}`);
const SuccessIcon = lazy(() => import(`globalcomponents/SuccessIcon/${layout}`));

const REASSIGN_API_ID = 'createCoDriverEventReassignment';

const CoDriverEventReassignmentModal = ({
  open,
  setOpen,
  entry,
  coDriverEventReassignment,
  companyDrivers,
  logIdPub,
  i18n,
  createCoDriverEventReassignment,
  getDailyLogDetails,
  apiStatus,
  fetchDailyLogDetailsApiId,
}) => {
  const [destinationDriver, setDestinationDriver] = useState(null);
  const [annotation, setAnnotation] = useState('');
  const [successOpen, setSuccessOpen] = useState(false);

  const hasReassignApiStatus = hasValue(apiStatus?.status, 'api', REASSIGN_API_ID);
  const coDriverOptions = mapCoDriversToOptions(coDriverEventReassignment?.coDrivers);

  const currentDriverId = entry?.driverId != null ? String(entry.driverId) : null;
  const isRequestEnabled =
    destinationDriver?.value &&
    annotation.trim().length > 0 &&
    String(destinationDriver.value) !== currentDriverId;

  useEffect(() => {
    if (open) {
      setDestinationDriver(null);
      setAnnotation('');
    }
  }, [open, entry?.id]);

  const resetForm = () => {
    setDestinationDriver(null);
    setAnnotation('');
  };

  const handleClose = () => {
    resetForm();
    setOpen(false);
  };

  const handleSubmit = () => {
    if (!entry?.eventId || !isRequestEnabled || hasReassignApiStatus?.isLoading) return;

    createCoDriverEventReassignment({
      apiId: REASSIGN_API_ID,
      data: {
        id: logIdPub,
        body: {
          eventId: entry.eventId,
          minutesFrom: entry.minutes_from,
          minutesTo: entry.minutes_to,
          destinationDriverId: parseInt(destinationDriver.value, 10),
          annotation: annotation.trim(),
        },
      },
      callback: () => {
        setOpen(false);
        setSuccessOpen(true);
        getDailyLogDetails({
          data: { id: logIdPub },
          apiId: fetchDailyLogDetailsApiId,
        });
        resetForm();
      },
    });
  };

  const eventFields = [
    { label: i18n.t('logs.timeFrom'), value: entry?.time_from || '-' },
    { label: i18n.t('logs.timeTo'), value: entry?.time_to || '-' },
    { label: i18n.t('logs.location'), value: entry?.location || '-' },
    { label: i18n.t('logs.notes'), value: entry?.note || '-' },
    {
      label: i18n.t('common.currentDriver'),
      value: getCurrentDriverLabel(entry, companyDrivers),
      fullWidth: true,
    },
  ];

  const reassignModalContent = (
    <div className="successpopup coDriverReassignModal">
      <div className="reassignModalHeader">
        <h2>{i18n.t('logs.reassignEvent')}</h2>
      </div>

      <div className="reassignSection">
        <h3 className="reassignSectionTitle">{i18n.t('logs.reassignEventDetails')}</h3>
        <div className="reassignEventCard">
          <div className="reassignEventCardHeader">
            <span className="reassignEventStatus">{entry?.status || 'DRIVING'}</span>
          </div>
          <div className="reassignEventGrid">
            {eventFields.map((item) => (
              <div
                key={item.label}
                className={`reassignEventField${item.fullWidth ? ' reassignEventFieldFull' : ''}`}
              >
                <span className="reassignEventLabel">{item.label}</span>
                <span className="reassignEventValue">{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="reassignSection reassignSectionForm">
        <h3 className="reassignSectionTitle">{i18n.t('logs.reassignRequestDetails')}</h3>
        <FormStyle.default.FieldsWrapper width="100%" className="reassignField">
          <Label variant="body1">{i18n.t('logs.destinationCoDriver')}</Label>
          <FormStyle.default.TextWrapper>
            <Select
              placeholder={i18n.t('common.pleaseSelect')}
              value={destinationDriver}
              suggestions={coDriverOptions}
              onChange={(value) => setDestinationDriver(value)}
              className="Selectclass"
            />
          </FormStyle.default.TextWrapper>
        </FormStyle.default.FieldsWrapper>
        <FormStyle.default.FieldsWrapper width="100%" className="reassignField annotationField">
          <Label variant="body1">{i18n.t('common.annotation')}</Label>
          <FormStyle.default.TextareaWrapper>
            <Input
              placeholder={i18n.t('common.annotation')}
              value={annotation}
              isNotDebounced
              handleOnChange={(value) => setAnnotation(value)}
              multiLine
              type="text"
            />
          </FormStyle.default.TextareaWrapper>
        </FormStyle.default.FieldsWrapper>
      </div>

      {hasReassignApiStatus?.errorMessage && (
        <Message type="error" message={i18n.t(hasReassignApiStatus.errorMessage)} />
      )}

      <div className="deletePopupBtn reassignModalActions">
        <div className="cancelBtn">
          <Button label={i18n.t('common.cancel')} type="reset" onClick={handleClose} />
        </div>
        <div className="deletebtn">
          <Button
            label={i18n.t('logs.reassign')}
            disabled={!isRequestEnabled || hasReassignApiStatus?.isLoading}
            onClick={handleSubmit}
          />
        </div>
      </div>
    </div>
  );

  const successModalContent = (
    <div className="successpopup">
      <SuccessIcon />
      <h2>{i18n.t('logs.reassignSuccess')}</h2>
      <div className="deletePopupBtn">
        <div className="deletebtn">
          <Button label={i18n.t('common.ok')} onClick={() => setSuccessOpen(false)} />
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Modal.default
        open={open}
        setOpen={(value) => {
          if (!value) handleClose();
        }}
        data={{
          content: reassignModalContent,
        }}
      />
      <Modal.default
        open={successOpen}
        setOpen={setSuccessOpen}
        data={{
          content: successModalContent,
        }}
        isSuccess
      />
    </>
  );
};

CoDriverEventReassignmentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  entry: PropTypes.object,
  coDriverEventReassignment: PropTypes.object,
  companyDrivers: PropTypes.object,
  logIdPub: PropTypes.string,
  i18n: PropTypes.object.isRequired,
  createCoDriverEventReassignment: PropTypes.func.isRequired,
  getDailyLogDetails: PropTypes.func.isRequired,
  apiStatus: PropTypes.object,
  fetchDailyLogDetailsApiId: PropTypes.string,
};

export default CoDriverEventReassignmentModal;
